/* MolCellQuest 問題データ — タンパク質・酵素
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "molecule", category: "protein", categoryName: "タンパク質・酵素" };

export const MOLECULE_PROTEIN = [
  {
    id: "pro01", ...CAT,
    title: "タンパク質の階層構造", diff: 95, tags: ["一次構造", "二次構造", "四次構造"],
    explain: `<p>タンパク質の構造の階層について、正しい組み合わせはどれか。</p>`,
    type: "radio",
    options: [
      "一次構造：αヘリックスやβシート ／ 二次構造：アミノ酸の配列",
      "一次構造：アミノ酸の配列 ／ 二次構造：αヘリックスやβシート ／ 四次構造：複数のサブユニットの会合",
      "三次構造：複数のポリペプチド鎖の会合 ／ 四次構造：立体的な折りたたみ",
      "二次構造：ジスルフィド結合による架橋のみ",
    ],
    answer: 1,
    hints: ["一次構造は「配列そのもの」です。", "四次構造は「複数の鎖が集まってできる構造」で、単量体タンパク質には存在しません。"],
    solution: `<p><b>【ステップ1】4つの階層</b></p>
               <table>
                 <tr><th>階層</th><th>内容</th><th>主に保つ結合</th></tr>
                 <tr><td>一次</td><td><b>アミノ酸の配列</b></td><td>ペプチド結合（共有結合）</td></tr>
                 <tr><td>二次</td><td><b>αヘリックス、βシート</b>など局所構造</td><td>主鎖どうしの水素結合</td></tr>
                 <tr><td>三次</td><td>1本の鎖全体の立体的な折りたたみ</td><td>疎水性相互作用、イオン結合、水素結合、S-S結合</td></tr>
                 <tr><td>四次</td><td><b>複数のサブユニットの会合</b></td><td>三次構造と同じ種類の非共有結合</td></tr>
               </table>
               <p><b>【ステップ2】二次構造を決めているもの</b></p>
               <p>αヘリックスもβシートも、側鎖ではなく<b>主鎖の C=O と N-H の水素結合</b>で保たれます。
               だからどんなアミノ酸配列でも原理的には取りうるのですが、
               実際にはプロリン（環構造で N-H を持たない）はヘリックスを壊しやすいなど、
               側鎖の性質が傾向を決めます。</p>
               <p><b>【ステップ3】アンフィンセンのドグマ</b></p>
               <p>リボヌクレアーゼを変性させてから変性剤を除くと、自発的に活性が戻りました。
               このことから <b>「一次構造が三次構造を決定する」</b> と結論されます。
               折りたたみに必要な情報は、すべて配列の中に書き込まれているのです。</p>
               <p class="tiny">ヘモグロビンは α2β2 の四量体なので四次構造を持ちますが、
               ミオグロビンは単量体なので四次構造を持ちません。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "pro02", ...CAT,
    title: "酵素の働きと活性化エネルギー", diff: 115, tags: ["活性化エネルギー", "触媒", "平衡"],
    explain: `<p>酵素が反応を速める仕組みについて、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "反応の活性化エネルギーを下げることで、反応速度を上げる。",
      "反応の自由エネルギー変化（ΔG）を負にすることで、反応を進める。",
      "反応の平衡点を生成物側にずらす。",
      "反応で自らが消費されることで、反応を進める。",
    ],
    answer: 0,
    hints: [
      "触媒の定義を思い出しましょう。反応の「速さ」を変えるのか、「行き着く先」を変えるのか。",
      "酵素があってもなくても、平衡定数は同じです。",
    ],
    solution: `<p><b>【ステップ1】触媒が変えるもの・変えないもの</b></p>
               <table>
                 <tr><th></th><th>酵素の影響</th></tr>
                 <tr><td>活性化エネルギー（Ea）</td><td><b>下げる</b></td></tr>
                 <tr><td>反応速度</td><td><b>上げる</b>（正逆とも同じ倍率で）</td></tr>
                 <tr><td>ΔG（自由エネルギー変化）</td><td>変えない</td></tr>
                 <tr><td>平衡定数・平衡点</td><td>変えない</td></tr>
                 <tr><td>酵素自身</td><td>消費されない</td></tr>
               </table>
               <p><b>【ステップ2】なぜ Ea だけを下げられるのか</b></p>
               <p>酵素は<b>遷移状態を安定化する</b>ことで働きます。
               基質そのものより、反応の途中で通過する不安定な遷移状態のほうに
               強く結合するように活性部位ができているのです。</p>
               <p>この考えの強力な証拠が<b>触媒抗体（アブザイム）</b>です。
               遷移状態に似せた安定な分子（遷移状態アナログ）を抗原として抗体を作らせると、
               その抗体が実際に触媒活性を示します。
               「遷移状態に結合できること」が触媒の本質だと直接示した実験です。</p>
               <p><b>【ステップ3】ΔG を変えないことの意味</b></p>
               <p>酵素は熱力学的に不利な反応（ΔG > 0）を単独では進められません。
               細胞はそれを、ATP加水分解のような有利な反応と<b>共役させる</b>ことで解決します。
               酵素が変えるのは「速さ」だけで、「どちらへ進むか」は熱力学が決めます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "pro03", ...CAT,
    title: "アミノ酸側鎖の性質", diff: 135, tags: ["アミノ酸", "疎水性", "側鎖"],
    explain: `<p>球状タンパク質が水中で折りたたまれるとき、
              分子の<b>内部</b>に埋め込まれやすいアミノ酸はどれか。</p>`,
    type: "radio",
    options: ["リシン（Lys）", "グルタミン酸（Glu）", "ロイシン（Leu）", "セリン（Ser）"],
    answer: 2,
    hints: [
      "水になじまない（疎水性の）側鎖を持つのはどれでしょう。",
      "分岐した炭化水素鎖だけからなる側鎖は、水を避けたがります。",
    ],
    solution: `<p><b>【ステップ1】各アミノ酸の側鎖を分類する</b></p>
               <table>
                 <tr><th>アミノ酸</th><th>側鎖</th><th>性質</th><th>位置</th></tr>
                 <tr><td>リシン</td><td>-（CH<sub>2</sub>）<sub>4</sub>NH<sub>3</sub><sup>+</sup></td><td>塩基性・正電荷</td><td>表面</td></tr>
                 <tr><td>グルタミン酸</td><td>-（CH<sub>2</sub>）<sub>2</sub>COO<sup>-</sup></td><td>酸性・負電荷</td><td>表面</td></tr>
                 <tr><td><b>ロイシン</b></td><td>-CH<sub>2</sub>CH(CH<sub>3</sub>)<sub>2</sub></td><td><b>疎水性</b></td><td><b>内部</b></td></tr>
                 <tr><td>セリン</td><td>-CH<sub>2</sub>OH</td><td>極性・非電荷</td><td>表面が多い</td></tr>
               </table>
               <p><b>【ステップ2】疎水効果が折りたたみの原動力</b></p>
               <p>疎水性側鎖が水に露出していると、周囲の水分子が
               規則正しい籠状構造を作らされ、<b>エントロピーが下がります</b>。
               疎水性側鎖どうしが内部に集まると水が解放され、
               系全体のエントロピーが増大します。</p>
               <p>つまり折りたたみの主な駆動力は「側鎖どうしが引き合う力」ではなく、
               <b>水が自由になりたがること</b>なのです。これを<b>疎水効果</b>といいます。</p>
               <p><b>【ステップ3】例外にも意味がある</b></p>
               <p>疎水性残基が<b>表面</b>にあるときは、多くの場合そこが機能部位です。</p>
               <ul>
                 <li>タンパク質どうしの結合面</li>
                 <li>膜貫通領域（脂質二重層に埋まる）</li>
                 <li>基質結合ポケット</li>
               </ul>
               <p>逆に荷電残基が<b>内部</b>にあれば、触媒残基か塩橋を作っている可能性が高い。
               「例外を探すと機能が見える」のがタンパク質構造の読み方です。</p>
               <p>したがって、正解は <b>ロイシン (C)</b> です。</p>`,
  },

  {
    id: "pro04", ...CAT,
    title: "ミカエリス・メンテン式と Km", diff: 200, tags: ["Km", "Vmax", "酵素反応速度論"],
    explain: `<p>ミカエリス・メンテン式に従う酵素について、<b>Km</b> の意味として最も適切なものはどれか。</p>
              <p style="text-align:center">v = V<sub>max</sub>[S] / (K<sub>m</sub> + [S])</p>`,
    type: "radio",
    options: [
      "最大反応速度そのもの",
      "反応速度が Vmax の半分になるときの基質濃度",
      "酵素1分子が1秒間に変換できる基質分子数",
      "酵素と基質の結合が最も強いときの解離定数の逆数",
    ],
    answer: 1,
    hints: [
      "式に [S] = Km を代入してみましょう。v はどうなりますか。",
      "v = Vmax·Km/(Km+Km) = Vmax/2 になります。",
    ],
    solution: `<p><b>【ステップ1】式に代入して確かめる</b></p>
               <p>[S] = K<sub>m</sub> とすると、<br>
               v = V<sub>max</sub>·K<sub>m</sub> / (K<sub>m</sub> + K<sub>m</sub>)
               = V<sub>max</sub>·K<sub>m</sub> / 2K<sub>m</sub> = <b>V<sub>max</sub> / 2</b></p>
               <p>つまり K<sub>m</sub> は<b>反応速度が最大値の半分になる基質濃度</b>です。</p>
               <p><b>【ステップ2】Km が示すもの</b></p>
               <p>K<sub>m</sub> が<b>小さい</b>ほど、低い基質濃度で最大速度の半分に達する
               ＝ <b>基質への親和性が高い</b>ことを意味します。</p>
               <table>
                 <tr><th>酵素</th><th>基質</th><th>Km</th><th>意味</th></tr>
                 <tr><td>ヘキソキナーゼ</td><td>グルコース</td><td>約 0.05 mM</td><td>低血糖でも働く（多くの組織）</td></tr>
                 <tr><td>グルコキナーゼ</td><td>グルコース</td><td>約 10 mM</td><td>高血糖のときだけ働く（肝・膵β細胞）</td></tr>
               </table>
               <p>同じ反応を触媒する酵素でも K<sub>m</sub> が違うことで、
               <b>組織ごとに異なる応答</b>を実現しています。
               肝臓のグルコキナーゼが高い K<sub>m</sub> を持つおかげで、
               食後の高血糖時にだけグリコーゲン合成が加速されるのです。</p>
               <p><b>【ステップ3】他の選択肢との区別</b></p>
               <ul>
                 <li>A ⇒ それは V<sub>max</sub> です。</li>
                 <li>C ⇒ それは <b>k<sub>cat</sub>（ターンオーバー数）</b>です。
                     触媒効率は k<sub>cat</sub>/K<sub>m</sub> で評価します。</li>
                 <li>D ⇒ K<sub>m</sub> は解離定数 K<sub>d</sub> に近いことはありますが、
                     厳密には (k<sub>-1</sub>+k<sub>cat</sub>)/k<sub>1</sub> であり、
                     「逆数」ではありません。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "pro05", ...CAT,
    title: "酵素阻害の様式を見分ける", diff: 260, tags: ["競合阻害", "非競合阻害", "Lineweaver-Burk"],
    explain: `<p>ある酵素に阻害剤を加えて反応速度を測ったところ、
              <b>Vmax は変化せず、Km だけが大きくなった</b>。</p>
              <p>この阻害様式と、その特徴の説明として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "競合阻害。阻害剤が活性部位に結合するため、基質濃度を上げれば阻害を打ち消せる。",
      "非競合阻害。阻害剤が活性部位以外に結合するため、基質濃度を上げても阻害を打ち消せない。",
      "不競合阻害。阻害剤は酵素-基質複合体にのみ結合する。",
      "競合阻害。基質濃度を上げても阻害は解消されない。",
    ],
    answer: 0,
    hints: [
      "Vmax が変わらない＝基質を十分に増やせば最大速度に到達できる、ということです。",
      "阻害剤と基質が同じ場所を取り合っているなら、基質が多ければ勝てます。",
    ],
    solution: `<p><b>【ステップ1】3つの阻害様式の指紋</b></p>
               <table>
                 <tr><th>様式</th><th>結合先</th><th>Vmax</th><th>Km(見かけ)</th><th>高[S]で解消</th></tr>
                 <tr><td><b>競合</b></td><td>遊離酵素の活性部位</td><td><b>不変</b></td><td><b>増加</b></td><td><b>する</b></td></tr>
                 <tr><td>非競合</td><td>E と ES の両方（別部位）</td><td>低下</td><td>不変</td><td>しない</td></tr>
                 <tr><td>不競合</td><td>ES のみ</td><td>低下</td><td>低下</td><td>しない</td></tr>
               </table>
               <p><b>【ステップ2】設問のデータを当てはめる</b></p>
               <p>「Vmax 不変、Km 増加」は<b>競合阻害</b>の指紋そのものです。</p>
               <p><b>【ステップ3】なぜそうなるのか</b></p>
               <ul>
                 <li><b>Km が増える</b>：阻害剤が活性部位を占めるので、
                     同じ速度を出すのにより多くの基質が要る＝見かけの親和性が下がる。</li>
                 <li><b>Vmax が変わらない</b>：基質濃度を十分に上げれば、
                     確率的に基質が阻害剤を追い出して活性部位を占められる。
                     だから最終的には元の最大速度に到達できる。</li>
               </ul>
               <p><b>【ステップ4】Lineweaver-Burk プロットで見る</b></p>
               <p>二重逆数プロット（1/v 対 1/[S]）では、</p>
               <ul>
                 <li>y切片 = 1/V<sub>max</sub> → 競合阻害では<b>変わらない（同じ点で交わる）</b></li>
                 <li>傾き = K<sub>m</sub>/V<sub>max</sub> → <b>急になる</b></li>
               </ul>
               <p><b>【ステップ5】臨床例</b></p>
               <p>メタノール中毒の治療では、<b>エタノール</b>を投与します。
               どちらもアルコール脱水素酵素の基質で、
               エタノールのほうが親和性が高いため競合阻害剤として働き、
               メタノールが有毒なホルムアルデヒドに変換されるのを防ぎます。
               「基質濃度を上げれば勝てる」という競合阻害の性質を、治療に使った例です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "pro06", ...CAT,
    title: "アロステリック制御と協同性", diff: 230, tags: ["アロステリック", "ヘモグロビン", "シグモイド"],
    explain: `<p>ヘモグロビンの酸素解離曲線は S字（シグモイド）を描くが、
              ミオグロビンは双曲線を描く。</p>
              <p>この違いの理由として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "ヘモグロビンのほうが酸素への親和性が常に高いため。",
      "ヘモグロビンは4量体で、1つのサブユニットへの酸素結合が他のサブユニットの親和性を高める（協同性）ため。",
      "ミオグロビンはヘムを持たないため。",
      "ヘモグロビンは酸素を化学的に酸化して結合するため。",
    ],
    answer: 1,
    hints: [
      "ミオグロビンは単量体、ヘモグロビンは4量体。この違いが効いています。",
      "S字カーブは「最初は結合しにくく、いったん結合しはじめると急に結合しやすくなる」ことを表します。",
    ],
    solution: `<p><b>【ステップ1】曲線の形が意味すること</b></p>
               <ul>
                 <li><b>双曲線（ミオグロビン）</b>：各結合部位が独立。低い酸素分圧でも高い飽和度。</li>
                 <li><b>S字（ヘモグロビン）</b>：低圧では結合しにくいが、
                     ある点を超えると急激に結合が進む＝<b>協同性</b>がある。</li>
               </ul>
               <p><b>【ステップ2】協同性の仕組み</b></p>
               <p>ヘモグロビンは α2β2 の4量体で、2つの状態を行き来します。</p>
               <ul>
                 <li><b>T状態（tense）</b>：酸素親和性が低い</li>
                 <li><b>R状態（relaxed）</b>：酸素親和性が高い</li>
               </ul>
               <p>1分子の酸素が結合するとサブユニット間の相互作用を通じて全体が
               T→R へ傾き、残りのサブユニットが酸素と結合しやすくなります。
               この「1つ結合すると次が結合しやすくなる」性質が S字を生みます。</p>
               <p><b>【ステップ3】なぜ S字が有利なのか</b></p>
               <p>これが生理的に決定的です。</p>
               <table>
                 <tr><th>場所</th><th>酸素分圧</th><th>飽和度</th></tr>
                 <tr><td>肺</td><td>約 100 mmHg</td><td>約 98%（しっかり積む）</td></tr>
                 <tr><td>組織</td><td>約 40 mmHg</td><td>約 75%（大きく放す）</td></tr>
               </table>
               <p>S字の急な部分がちょうどこの範囲にかかるため、
               <b>わずかな分圧差で大量の酸素を受け渡せます</b>。
               もし双曲線だったら、肺でも組織でもほぼ飽和したままで、
               酸素をほとんど放出できません。ミオグロビンが筋肉内の
               「貯蔵庫」に向くのは、まさに放しにくいからです。</p>
               <p class="tiny">さらに H⁺・CO₂・2,3-BPG が T状態を安定化し、
               代謝の盛んな組織ほど酸素が放出されやすくなります（ボーア効果）。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "pro07", ...CAT,
    title: "シャペロンとタンパク質の凝集", diff: 210, tags: ["シャペロン", "熱ショックタンパク質", "凝集"],
    explain: `<p>分子シャペロンの働きとして、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "タンパク質の最終的な立体構造の情報を提供する。",
      "正しく折りたたまれていないタンパク質の疎水性領域に結合し、誤った凝集を防いで折りたたみを助ける。",
      "折りたたみに失敗したタンパク質を直接切断して分解する。",
      "アミノ酸配列を書き換えて、折りたたみやすい配列にする。",
    ],
    answer: 1,
    hints: [
      "アンフィンセンの実験から、構造の情報はどこにあると分かっていましたか。",
      "折りたたみ途中のタンパク質は、本来内部に隠れるべき疎水性領域を露出しています。",
    ],
    solution: `<p><b>【ステップ1】シャペロンは情報を与えない</b></p>
               <p>立体構造の情報は<b>アミノ酸配列そのものに含まれています</b>（アンフィンセンのドグマ）。
               シャペロンは正解を教えるのではなく、
               <b>間違った道に行かないよう手助けする</b>役割です。</p>
               <p><b>【ステップ2】何が問題なのか</b></p>
               <p>細胞内は<b>分子混雑（macromolecular crowding）</b>状態で、
               タンパク質濃度は 300〜400 mg/mL にもなります。
               折りたたみ途中で露出した疎水性領域どうしが出会うと、
               本来の相手（同じ鎖の別の部分）ではなく<b>他の分子と会合して凝集</b>してしまいます。</p>
               <p><b>【ステップ3】シャペロンの作戦</b></p>
               <ul>
                 <li><b>Hsp70</b>：露出した疎水性ストレッチに ATP依存的に結合・解離を繰り返し、
                     凝集を防ぎながら折りたたみの機会を与える</li>
                 <li><b>シャペロニン（GroEL/GroES、TRiC）</b>：樽状の空洞に1分子を隔離し、
                     <b>他の分子と出会わない環境</b>で折りたたませる</li>
               </ul>
               <p>どちらも「正解を教える」のではなく「失敗の機会を減らす」設計です。</p>
               <p><b>【ステップ4】熱ショックタンパク質と疾患</b></p>
               <p>多くのシャペロンは <b>Hsp（熱ショックタンパク質）</b>と呼ばれます。
               高温で変性が増えると需要が高まるため、熱で誘導されるからです。</p>
               <p>この防御が破綻すると凝集体が蓄積します。
               アルツハイマー病（Aβ、タウ）、パーキンソン病（α-シヌクレイン）、
               ハンチントン病（ポリグルタミン）はいずれも
               <b>タンパク質のミスフォールディングと凝集</b>を共通の基盤に持ちます。</p>
               <p class="tiny">なお C の「切断して分解」はプロテアソームやプロテアーゼの役割で、
               シャペロンとは別系統（ただし協調して働きます）。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "pro08", ...CAT,
    title: "ユビキチン・プロテアソーム系", diff: 250, tags: ["ユビキチン", "プロテアソーム", "分解"],
    explain: `<p>ユビキチン・プロテアソーム系によるタンパク質分解について、<b>誤っている</b>記述はどれか。</p>`,
    type: "radio",
    options: [
      "E1・E2・E3 の三段階の酵素反応でユビキチンが基質に付加される。",
      "基質特異性は主に E3 ユビキチンリガーゼが担う。",
      "K48 結合型のポリユビキチン鎖は、主に分解のシグナルとなる。",
      "プロテアソームによる分解にはATPを必要としない。",
    ],
    answer: 3,
    hints: [
      "分解は自発的に進みそうな反応ですが、なぜエネルギーが要るのでしょう。",
      "26Sプロテアソームの19S部分には ATPアーゼが含まれています。何のためでしょうか。",
    ],
    solution: `<p><b>【ステップ1】ユビキチン化のカスケード</b></p>
               <table>
                 <tr><th>酵素</th><th>働き</th><th>ヒトでの種類数</th></tr>
                 <tr><td>E1（活性化酵素）</td><td>ATP を使いユビキチンを活性化</td><td>2</td></tr>
                 <tr><td>E2（結合酵素）</td><td>ユビキチンを受け取る</td><td>約40</td></tr>
                 <tr><td><b>E3（リガーゼ）</b></td><td><b>基質を認識</b>してユビキチンを転移</td><td><b>600以上</b></td></tr>
               </table>
               <p>E3 の種類が圧倒的に多いことが、そのまま
               <b>基質特異性を E3 が担っている</b>ことを物語っています。</p>
               <p><b>【ステップ2】ユビキチン鎖の「暗号」</b></p>
               <p>ユビキチンには7つのリジンがあり、どこでつなぐかで意味が変わります。</p>
               <ul>
                 <li><b>K48 鎖</b>：プロテアソームによる<b>分解</b>シグナル</li>
                 <li><b>K63 鎖</b>：DNA修復、シグナル伝達、オートファジー（分解以外）</li>
                 <li><b>モノユビキチン化</b>：エンドサイトーシス、ヒストン修飾</li>
               </ul>
               <p>「ユビキチン＝分解の目印」は単純化しすぎで、
               実際は多様な情報を運ぶ<b>言語</b>です。</p>
               <p><b>【ステップ3】D が誤りである理由</b></p>
               <p>26Sプロテアソームの<b>19S制御複合体には6種のATPアーゼ</b>が含まれ、
               ATP を消費します。用途は2つです。</p>
               <ol>
                 <li>基質を<b>アンフォールディング</b>する（折りたたまれたままでは通れない）</li>
                 <li>細い触媒室（20Sコア）へ<b>引き込む</b></li>
               </ol>
               <p>ATP を要求することには重要な意味があります。
               分解は<b>不可逆</b>なので、誤って必要なタンパク質を壊さないよう、
               エネルギーを使った厳格な選別を課しているのです。</p>
               <p class="tiny">プロテアソーム阻害剤ボルテゾミブが多発性骨髄腫に効くのは、
               抗体を大量産生する形質細胞が、不良品の処理をこの系に強く依存しているためです。</p>
               <p>したがって、誤っているのは <b>D</b> です。</p>`,
  },

  {
    id: "pro09", ...CAT,
    title: "希釈系列から濃度を計算する", diff: 145, tags: ["計算", "希釈", "実験"],
    explain: `<p><b>2 mg/mL</b> のタンパク質溶液がある。これを使って
              <b>0.05 mg/mL</b> の溶液を <b>1 mL</b> 作りたい。</p>
              <p>元の溶液は何 μL 必要か。半角数字で答えなさい（単位は不要）。</p>`,
    type: "text",
    answer: ["25"],
    hints: [
      "C₁V₁ = C₂V₂ の関係を使いましょう。",
      "2 × V₁ = 0.05 × 1000 μL相当。単位をそろえて計算します。",
    ],
    solution: `<p><b>【ステップ1】希釈の基本式</b></p>
               <p>希釈しても<b>溶質の総量は変わりません</b>。<br>
               C<sub>1</sub>V<sub>1</sub> = C<sub>2</sub>V<sub>2</sub></p>
               <p><b>【ステップ2】代入する</b></p>
               <p>2 mg/mL × V<sub>1</sub> = 0.05 mg/mL × 1 mL<br>
               V<sub>1</sub> = 0.05 / 2 = <b>0.025 mL</b></p>
               <p><b>【ステップ3】単位を直す</b></p>
               <p>0.025 mL × 1000 = <b>25 μL</b></p>
               <p>つまり元の溶液 25 μL に緩衝液 975 μL を加えて全量 1 mL にします。</p>
               <p><b>【ステップ4】検算</b></p>
               <p>希釈倍率は 2 ÷ 0.05 = 40倍。1 mL ÷ 40 = 0.025 mL = 25 μL。合っています。</p>
               <p class="tiny">実験上の注意：40倍希釈を1回で行うと、
               25 μL の分注誤差がそのまま最終濃度に響きます。
               精度が要るときは 10倍 → 4倍のように<b>段階希釈</b>にすると、
               各ステップで扱う量が大きくなり誤差が小さくなります。</p>
               <p>したがって、答えは <b>25</b> です。</p>`,
  },

  {
    id: "pro10", ...CAT,
    title: "プリオンという例外", diff: 305, tags: ["プリオン", "構造変換", "セントラルドグマ"],
    explain: `<p>プリオン病では、正常型プリオンタンパク質（PrP<sup>C</sup>）が
              異常型（PrP<sup>Sc</sup>）に変換され、それが伝播していく。</p>
              <p>この現象がセントラルドグマに対して持つ意味として、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "タンパク質から核酸へ配列情報が逆流するため、セントラルドグマの反例である。",
      "アミノ酸配列は同一のまま立体構造だけが伝播するので、配列情報の流れは破っていないが、「配列が構造を一意に決める」という前提には例外があることを示す。",
      "PrP<sup>Sc</sup> は独自の核酸を持っており、それが複製されている。",
      "PrP<sup>Sc</sup> は PrP<sup>C</sup> の遺伝子に変異を導入することで増える。",
    ],
    answer: 1,
    hints: [
      "PrP^C と PrP^Sc は、アミノ酸配列が同じでしょうか、違うでしょうか。",
      "伝播しているのは「配列の情報」でしょうか、それとも「折りたたみ方の情報」でしょうか。",
    ],
    solution: `<p><b>【ステップ1】2つの型の関係</b></p>
               <table>
                 <tr><th></th><th>PrP<sup>C</sup>（正常）</th><th>PrP<sup>Sc</sup>（異常）</th></tr>
                 <tr><td>アミノ酸配列</td><td colspan="2" style="text-align:center"><b>まったく同一</b></td></tr>
                 <tr><td>二次構造</td><td>αヘリックスに富む</td><td><b>βシートに富む</b></td></tr>
                 <tr><td>溶解性</td><td>可溶</td><td>不溶・凝集</td></tr>
                 <tr><td>プロテアーゼ耐性</td><td>感受性</td><td><b>耐性</b></td></tr>
               </table>
               <p><b>【ステップ2】何が伝播しているのか</b></p>
               <p>PrP<sup>Sc</sup> は PrP<sup>C</sup> に接触して<b>鋳型として働き</b>、
               同じβシート構造へと変換させます。増えたものがまた次を変換し、連鎖的に広がる。</p>
               <p>ここで伝播しているのは<b>立体構造という情報</b>であって、
               <b>アミノ酸配列の情報ではありません</b>。核酸は一切関与しません。</p>
               <p><b>【ステップ3】セントラルドグマとの関係を正確に述べる</b></p>
               <p>セントラルドグマが禁じているのは
               <b>「タンパク質から核酸への<u>配列</u>情報の流れ」</b>です。
               プリオンはこれを破っていません — PrP 遺伝子の配列は何も変わらないからです。</p>
               <p>プリオンが覆したのは別の前提、すなわち
               <b>「一次構造が三次構造を一意に決める」</b>というアンフィンセンのドグマのほうです。
               同じ配列が2つの安定な構造を取りうる。しかも一方が他方を自分の形に変えられる。
               「感染性を持つのに遺伝物質を持たない病原体」という、当初は受け入れがたかった結論です。</p>
               <p><b>【ステップ4】広がり</b></p>
               <p>プリオン様の構造伝播は、いまや病気だけの話ではありません。</p>
               <ul>
                 <li>アルツハイマー病のタウ、パーキンソン病のα-シヌクレインも
                     細胞から細胞へ構造を伝播させると考えられている</li>
                 <li>酵母の [PSI+] など、<b>有益に働く</b>プリオンも見つかっている</li>
               </ul>
               <p>「タンパク質の構造そのものが遺伝しうる情報である」という、
               エピジェネティクスの一形態として理解が広がっています。</p>
               <p class="tiny">プルシナーはこの発見で1997年のノーベル生理学・医学賞を受賞しました。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "pro11", ...CAT,
    title: "アンフィンゼンのドグマ", diff: 155, tags: ["フォールディング", "アンフィンゼン", "リボヌクレアーゼ"],
    explain: `<p>アンフィンゼンは、リボヌクレアーゼを変性剤と還元剤で完全に変性させたあと、
              それらを取り除くと活性が戻ることを示した。</p>
              <p>この実験が示したこととして最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "タンパク質の高次構造は、そのアミノ酸配列（一次構造）によって決定される。",
      "タンパク質の高次構造は、シャペロンがなければ決して形成されない。",
      "タンパク質の高次構造は、リボソーム上でのみ決定される。",
      "変性したタンパク質は決して元に戻らない。",
    ],
    answer: 0,
    hints: [
      "実験では、細胞の中の装置は何も加えていません。それでも活性が戻りました。",
      "「情報はどこに書かれているか」を問う実験です。",
    ],
    solution: `<p><b>【ステップ1】実験を読む</b></p>
               <ol>
                 <li>尿素（変性剤）と β-メルカプトエタノール（還元剤）で処理
                 → 高次構造が崩れ、<b>ジスルフィド結合も切れて</b>活性を失う</li>
                 <li>両方を透析で取り除く → <b>自発的に</b>元の構造に戻り、活性が回復する</li>
               </ol>
               <p>試験管の中には他のタンパク質もエネルギー源もありません。
               それでも正しい構造に戻るということは、<b>折りたたみに必要な情報は
               アミノ酸配列そのものに書かれている</b>ことを意味します。
               これが<b>アンフィンゼンのドグマ</b>です。</p>
               <p><b>【ステップ2】順番が大事だった証拠</b></p>
               <p>還元剤だけを先に除いて尿素の存在下でジスルフィド結合を作らせると、
               <b>間違った組み合わせ</b>で結合ができ、活性は戻りませんでした（スクランブル体）。
               正しい立体構造が先にでき、その結果として正しいシステイン対が近づく、という順序を示しています。</p>
               <p><b>【ステップ3】シャペロンとの関係</b></p>
               <p>「配列で決まる」ことと「シャペロンが要る」ことは矛盾しません。
               細胞内は<b>タンパク質が極めて高濃度</b>で、折りたたみ途中の疎水面が
               他の分子とくっついて凝集してしまいます。
               シャペロンは<b>正解を教えるのではなく、間違った相互作用を防いで
               自力で折りたためる環境を用意する</b>役割です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "pro12", ...CAT,
    title: "小胞体のシャペロンと品質管理", diff: 230, tags: ["シャペロン", "BiP", "カルネキシン"],
    explain: `<p>小胞体で働く分子シャペロンと、その特徴の組み合わせとして<b>誤っている</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "HSP47 ─ 糖鎖を認識してフォールディングを助ける、レクチン型シャペロン",
      "BiP ─ 疎水性領域に結合してATP依存的に折りたたみを助ける、Hsp70ファミリー",
      "カルネキシン／カルレティキュリン ─ N型糖鎖の末端グルコースを認識するレクチン型シャペロン",
      "プロテインジスルフィドイソメラーゼ（PDI）─ ジスルフィド結合の形成と組み替えを触媒する",
    ],
    answer: 0,
    hints: [
      "HSP47 が担当する基質は非常に特殊で、1種類に限られています。",
      "「誤っているもの」を選ぶ問題です。",
    ],
    solution: `<p><b>【ステップ1】HSP47 の正体</b></p>
               <p>HSP47 は<b>コラーゲン特異的</b>な分子シャペロンです。
               糖鎖ではなく<b>コラーゲンの三重らせん</b>を認識し、
               その形成を助けて、正しく巻けたものだけを小胞体から送り出します。
               レクチン型（糖鎖認識型）ではないので、A が誤りです。</p>
               <p><b>【ステップ2】小胞体シャペロンの整理</b></p>
               <table>
                 <tr><th>シャペロン</th><th>認識するもの</th><th>特徴</th></tr>
                 <tr><td><b>BiP</b>（GRP78）</td><td>露出した<b>疎水性領域</b></td><td>Hsp70型。ATP依存。UPRの3センサーを抑えている見張り役でもある</td></tr>
                 <tr><td><b>カルネキシン</b>（膜型）<br><b>カルレティキュリン</b>（可溶型）</td><td><b>N型糖鎖</b>のグルコース1個残った形</td><td>レクチン型。グルコシダーゼ／UGGT と組んで<b>品質管理サイクル</b>を回す</td></tr>
                 <tr><td><b>HSP47</b></td><td><b>コラーゲン</b></td><td>基質特異的</td></tr>
                 <tr><td><b>PDI</b></td><td>システイン</td><td>ジスルフィド結合の形成・組み替え（酵素）</td></tr>
               </table>
               <p><b>【ステップ3】カルネキシンサイクル</b></p>
               <p>糖鎖のグルコースが1個だけ残っているとカルネキシンが結合し、折りたたみを助けます。
               グルコシダーゼIIがそれを外すと離れますが、まだ折りたたみが不十分なら
               <b>UGGT</b> がグルコースを付け直して<b>もう一度カルネキシンに掴ませます</b>。
               何度繰り返しても直らないものは糖鎖を刈り込まれ、<b>ERAD</b> へ回されます。
               「合格するまでやり直させ、見込みがなければ廃棄する」という品質管理の仕組みです。</p>
               <p>したがって、<b>誤っている</b>のは <b>A</b> です。</p>`,
  },
];
